import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { AppCtx } from '../../../AppCtx';
import yjsDocumentService from '../../../infrastructure/sync/YjsDocumentService';
import { getWorkspaceId } from '../../../shared/services/dbUtils';
import { ThemeContext } from '../../../ThemeContext';
import './YjsDebugViewStyles.css';

// TreeNode component to recursively render the YJS document structure
const TreeNode = ({ name, value, level = 0, isExpanded = false }) => {
  const [expanded, setExpanded] = useState(isExpanded);
  const { theme } = useContext(ThemeContext);
  const nodeStyle = {
    marginLeft: `${level * 20}px`,
    paddingLeft: '10px',
    borderLeft: `1px solid ${theme === 'dark' ? '#555' : '#ddd'}`,
    color: theme === 'dark' ? '#fff' : '#000',
  };

  // Function to render different types of values
  const renderValue = () => {
    // Handle null and undefined
    if (value === null) return <span className="yjs-debug-null">null</span>;
    if (value === undefined) return <span className="yjs-debug-undefined">undefined</span>;

    // Handle different types
    switch (typeof value) {
      case 'string':
        return <span className="yjs-debug-string">"{value}"</span>;
      case 'number':
        return <span className="yjs-debug-number">{value}</span>;
      case 'boolean':
        return <span className="yjs-debug-boolean">{value.toString()}</span>;
      case 'object':
        if (Array.isArray(value)) {
          if (value.length === 0) return <span className="yjs-debug-array">[]</span>;
          return (
            <div>
              <span className="yjs-debug-array">[{expanded ? '' : `...${value.length} items`}]</span>
              {expanded && (
                <div>
                  {value.map((item, index) => (
                    <TreeNode 
                      key={`${name}-${index}`} 
                      name={`${index}`} 
                      value={item} 
                      level={level + 1} 
                    />
                  ))}
                </div>
              )}
            </div>
          );
        } else if (value instanceof Map || value instanceof Set || (value && typeof value.forEach === 'function')) {
          // Handle YJS data types and Map/Set
          const entries = [];
          try {
            value.forEach((val, key) => {
              entries.push({ key, val });
            });
          } catch (e) {
            // Fallback for objects that don't properly implement forEach
            return <span className="yjs-debug-object">{`[${value.constructor.name}]`}</span>;
          }
          
          return (
            <div>
              <span className="yjs-debug-object">{value.constructor.name} {expanded ? '' : `...${entries.length} items`}</span>
              {expanded && entries.map((entry, index) => (
                <TreeNode 
                  key={`${name}-${entry.key || index}`} 
                  name={`${entry.key !== undefined ? entry.key : index}`} 
                  value={entry.val} 
                  level={level + 1} 
                />
              ))}
            </div>
          );
        } else if (value && value.toString && value.toString() !== '[object Object]') {
          // For objects with custom toString()
          return <span className="yjs-debug-custom">{value.toString()}</span>;
        } else if (value) {
          // For regular objects
          const keys = Object.keys(value);
          return (
            <div>
              <span className="yjs-debug-object">
                {`{${expanded ? '' : `...${keys.length} properties`}}`}
              </span>
              {expanded && keys.map(key => (
                <TreeNode 
                  key={`${name}-${key}`} 
                  name={key} 
                  value={value[key]} 
                  level={level + 1}
                />
              ))}
            </div>
          );
        }
        return <span className="yjs-debug-empty">{'{}'}</span>;
      default:
        return <span className="yjs-debug-unknown">{String(value)}</span>;
    }
  };

  // Only show toggle for objects and arrays
  const canExpand = value !== null && 
    typeof value === 'object' && 
    (Object.keys(value || {}).length > 0 || 
     (Array.isArray(value) && value.length > 0) ||
     value instanceof Map || 
     value instanceof Set);

  return (
    <div className="yjs-debug-node" style={nodeStyle}>
      <div className="yjs-debug-line">
        {canExpand && (
          <span 
            className="yjs-debug-toggle" 
            onClick={() => setExpanded(!expanded)}
            title={expanded ? 'Zwiń' : 'Rozwiń'}
          >
            {expanded ? '▼' : '►'}
          </span>
        )}
        <span className="yjs-debug-name">{name}: </span>
        {renderValue()}
      </div>
    </div>
  );
};

// Main component to display YJS debug information
const YjsDebugViewComponent = () => {
  const [docStructure, setDocStructure] = useState(null);
  const [workspaceId, setWorkspaceId] = useState(null);
  const [refreshCounter, setRefreshCounter] = useState(0);
  const [clientStats, setClientStats] = useState({active: 0, inactive: 0, total: 0});
  const [selectedClientsForCleanup, setSelectedClientsForCleanup] = useState({});
  const [isCleanupMode, setIsCleanupMode] = useState(false);
  const [filterRegex, setFilterRegex] = useState('');
  const [filteredStructure, setFilteredStructure] = useState(null);
  const [isFilterActive, setIsFilterActive] = useState(false);
  const [filterError, setFilterError] = useState(null);
  const { currentSessionId } = useContext(AppCtx);
  const isActive = useLocation().pathname === '/app/debug-view';
  const filterInputRef = useRef(null);

  useEffect(() => {
    if (!isActive) return;
    
    const wsId = getWorkspaceId();
    setWorkspaceId(wsId);
    
    if (!wsId) return;

    try {
      // Connect to document
      const { doc, provider } = yjsDocumentService.connectToDocument(wsId);
      
      // Create a representation of the YJS document
      const structure = {
        workspaceId: wsId,
        doc: extractDocStructure(doc),
        connection: {
          connected: provider.wsconnected,
          synced: provider.synced,
          awareness: {
            clients: extractAwarenessClients(provider.awareness)
          }
        }
      };
      
      setDocStructure(structure);
      setFilteredStructure(structure);

      // Calculate client statistics
      const activeClients = provider.awareness ? provider.awareness.getStates().size : 0;
      const totalClients = structure.doc.clients ? Object.keys(structure.doc.clients).length : 0;
      
      setClientStats({
        active: activeClients,
        inactive: totalClients - activeClients,
        total: totalClients
      });
      
      // Subscribe to document updates
      const unsubDocEvents = yjsDocumentService.subscribeToDocumentEvents(wsId, () => {
        setRefreshCounter(prev => prev + 1);
      });
      
      // Subscribe to awareness events
      const unsubAwareness = yjsDocumentService.subscribeToAwarenessEvents(wsId, () => {
        setRefreshCounter(prev => prev + 1);
      });
      
      return () => {
        unsubDocEvents();
        unsubAwareness();
      };
    } catch (error) {
      console.error('Error loading YJS document structure:', error);
    }
  }, [isActive, currentSessionId, refreshCounter]);

  // Apply filter when docStructure or filterRegex changes
  useEffect(() => {
    if (!docStructure) return;
    
    if (!filterRegex || filterRegex.trim() === '') {
      setFilteredStructure(docStructure);
      setIsFilterActive(false);
      setFilterError(null);
      return;
    }

    try {
      const regex = new RegExp(filterRegex, 'i');
      setFilterError(null);
      
      // Create a deep copy of the document structure
      const filteredDoc = JSON.parse(JSON.stringify(docStructure));
      
      // Filter the share data only
      if (filteredDoc.doc && filteredDoc.doc.share) {
        const filteredShare = {};
        
        for (const [key, value] of Object.entries(filteredDoc.doc.share)) {
          if (regex.test(key)) {
            filteredShare[key] = value;
          }
        }
        
        filteredDoc.doc.share = filteredShare;
      }
      
      setFilteredStructure(filteredDoc);
      setIsFilterActive(true);
    } catch (error) {
      setFilterError(`Invalid regex: ${error.message}`);
      setFilteredStructure(docStructure);
      setIsFilterActive(false);
    }
  }, [docStructure, filterRegex]);

  // Function to extract document structure
  const extractDocStructure = (doc) => {
    if (!doc) return null;
    
    const structure = {
      // Basic document info
      guid: doc.guid,
      clientID: doc.clientID,
      gc: doc.gc,
      gcEnabled: doc.gcEnabled,
      shouldLoad: doc.shouldLoad,
      autoLoad: doc.autoLoad,
      
      // Store information
      store: {
        clients: {
          size: doc.store.clients.size,
        },
        // Include structured storage info
        pendingStructs: doc.store.pendingStructs ? Array.from(doc.store.pendingStructs) : [],
        pendingDs: doc.store.pendingDs ? Array.from(doc.store.pendingDs) : []
      },
      
      // Document collections
      collections: {},
      subdocs: Array.from(doc.subdocs).map(subdoc => ({
        guid: subdoc.guid, 
        clientID: subdoc.clientID,
        loaded: subdoc.loaded,
        hasSubscriber: subdoc._hasSubscriber
      })),

      // Share data - the core of YJS document
      share: {},
      
      // Client information
      clients: {},
      
      // YJS Maps by key
      maps: {},
      
      // YJS Texts 
      texts: {},
      
      // User settings and client info (special collections)
      userSettings: {},
      clientInfo: {},
      
      // Advanced attributes
      transaction: doc._transaction ? {
        beforeState: doc._transaction.beforeState,
        deleteSet: doc._transaction.deleteSet,
        afterState: doc._transaction.afterState,
        changed: doc._transaction.changed,
        changedParentTypes: doc._transaction.changedParentTypes,
        origin: String(doc._transaction.origin || 'null'),  
      } : null,
      
      // Document statistics
      statistics: {
        clientsCount: doc.store.clients.size,
        shareKeysCount: doc.share.size,
      }
    };
    
    // Extract document collections 
    try {
      for (const [key, value] of Object.entries(doc)) {
        // Skip internal properties and functions
        if (key.startsWith('_') || typeof value === 'function') continue;
        
        // Skip already extracted properties
        if (['clientID', 'gc', 'guid', 'share', 'store', 'subdocs'].includes(key)) continue;
        
        // Add any remaining collections to the structure
        if (value && typeof value === 'object' && !Array.isArray(value)) {
          structure.collections[key] = {
            type: value.constructor ? value.constructor.name : typeof value,
            size: value.size || Object.keys(value).length || 'unknown'
          };
        }
      }
    } catch (e) {
      console.error('Error extracting document collections:', e);
      structure.collectionsError = e.message;
    }
    
    // Extract all share data (the core YJS data)
    try {
      for (const [name, type] of doc.share.entries()) {
        let content = null;
        
        // Use duck typing instead of constructor name checking
        const isYMap = typeof type.get === 'function' && typeof type.set === 'function' && !Array.isArray(type);
        const isYArray = typeof type.push === 'function' && typeof type.insert === 'function';
        const isYText = typeof type.toString === 'function' && typeof type.toDelta === 'function' && typeof type.insert === 'function';
        
        let typeDetails = {
          type: type.constructor ? type.constructor.name : 'Unknown',
          length: type.length || type.size || 'unknown',
          keyPrefix: name.includes(':') ? name.split(':')[0] : null,
          keyTarget: name.includes(':') ? name.split(':')[1] : null
        };
        
        // Extract actual content based on the type
        if (isYMap) {
          // For YMap, extract key-value pairs
          content = {};
          type.forEach((value, key) => {
            content[key] = value;
          });
        } else if (isYArray) {
          // For YArray, extract array elements
          content = Array.from(type);
        } else if (isYText) {
          // For YText, extract text content
          content = type.toString();
        }
        
        // Store both type details and content
        structure.share[name] = {
          ...typeDetails,
          content
        };
      }
    } catch (e) {
      console.error('Error extracting share data:', e);
    }
    
    // Extract clients data
    try {
      doc.store.clients.forEach((client, clientID) => {
        structure.clients[clientID] = {
          id: clientID,
          itemsCount: client.length,
          // Extract individual structs for detailed analysis
          structs: Array.from(client).map(struct => ({
            id: struct.id,
            length: struct.length,
            deleted: struct.deleted,
            type: struct.constructor.name
          }))
        };
      });
    } catch (e) {
      console.error('Error extracting clients data:', e);
    }
    
    // Extract all maps
    try {
      doc._map.forEach((value, key) => {
        const mapContent = {};
        try {
          value.forEach((val, mapKey) => {
            mapContent[mapKey] = val;
          });
          structure.maps[key] = mapContent;
        } catch (e) {
          structure.maps[key] = `[Error: ${e.message}]`;
        }
      });
    } catch (e) {
      console.error('Error extracting maps:', e);
    }
    
    // Extract text content
    try {
      for (const [name, text] of doc.share.entries()) {
        if (text.constructor.name === 'YText') {
          try {
            structure.texts[name] = {
              content: text.toString(),
              length: text.length,
              delta: text.toDelta()  // Show formatting if available
            };
          } catch (e) {
            structure.texts[name] = `[Error: ${e.message}]`;
          }
        }
      }
    } catch (e) {
      console.error('Error extracting text content:', e);
    }
    
    // Extract user settings (special maps with user-specific data)
    try {
      for (const key of Object.keys(structure.maps)) {
        if (key.startsWith('userSettings-')) {
          const deviceId = key.replace('userSettings-', '');
          structure.userSettings[deviceId] = structure.maps[key];
        }
        
        if (key.startsWith('clientInfo-')) {
          const deviceId = key.replace('clientInfo-', '');
          structure.clientInfo[deviceId] = structure.maps[key];
        }
      }
    } catch (e) {
      console.error('Error extracting user settings:', e);
    }
    
    return structure;
  };
  
  // Function to extract awareness clients
  const extractAwarenessClients = (awareness) => {
    if (!awareness) return [];
    
    const clients = [];
    awareness.getStates().forEach((state, clientId) => {
      clients.push({
        clientId,
        user: state.user
      });
    });
    
    return clients;
  };

  const handleRefresh = () => {
    setRefreshCounter(prev => prev + 1);
  };

  const toggleCleanupMode = () => {
    setIsCleanupMode(!isCleanupMode);
    setSelectedClientsForCleanup({});
  };

  const toggleClientSelection = (clientId) => {
    setSelectedClientsForCleanup(prev => {
      const newSelection = { ...prev };
      if (newSelection[clientId]) {
        delete newSelection[clientId];
      } else {
        newSelection[clientId] = true;
      }
      return newSelection;
    });
  };
  
  const cleanupSelectedClients = () => {
    const selectedIds = Object.keys(selectedClientsForCleanup);
    if (selectedIds.length === 0) return;
    
    try {
      // Get document and provider
      const { doc } = yjsDocumentService.connectToDocument(workspaceId);
      
      // This is a dangerous operation that should only be performed by admins
      // It forces removal of client data from the YJS document
      if (window.confirm(`WARNING: You are about to delete ${selectedIds.length} clients from the YJS document. This operation cannot be undone and may cause data loss if the clients are still active. Continue?`)) {
        
        // We need to perform a low-level cleanup
        // Note: This is an advanced operation that manipulates YJS internals
        selectedIds.forEach(clientId => {
          try {
            // Remove client from store
            if (doc.store.clients.has(clientId)) {
              doc.store.clients.delete(clientId);
              console.log(`Removed client ${clientId} from document`);
            }
            
            // Clear associated awareness state if exists
            const provider = yjsDocumentService.providers.get(workspaceId);
            if (provider && provider.awareness && provider.awareness.getStates().has(parseInt(clientId))) {
              provider.awareness.removeState(parseInt(clientId));
              console.log(`Removed client ${clientId} from awareness`);
            }
          } catch (err) {
            console.error(`Failed to remove client ${clientId}:`, err);
          }
        });
        
        // Reset selection and refresh
        setSelectedClientsForCleanup({});
        setRefreshCounter(prev => prev + 1);
      }
    } catch (error) {
      console.error('Error cleaning up clients:', error);
      alert(`Failed to clean up clients: ${error.message}`);
    }
  };

  const handleFilterChange = (e) => {
    setFilterRegex(e.target.value);
  };

  const clearFilter = () => {
    setFilterRegex('');
    if (filterInputRef.current) {
      filterInputRef.current.value = '';
      filterInputRef.current.focus();
    }
  };

  if (!filteredStructure) {
    return (
      <div className="yjs-debug-container">
        <h1>YJS Debug View</h1>
        <p>Loading document structure... If this takes too long, there might be no active YJS session.</p>
        <button onClick={handleRefresh}>Refresh</button>
      </div>
    );
  }

  const activeClientsArray = filteredStructure.connection.awareness.clients || [];
  const activeClientIds = new Set(activeClientsArray.map(c => c.clientId.toString()));
  
  // Get all client IDs from the document structure
  const allClientIds = Object.keys(filteredStructure.doc.clients || {});
  
  // Separate active and inactive clients
  const inactiveClientIds = allClientIds.filter(id => !activeClientIds.has(id));

  // Get info about share collection
  const shareKeysCount = Object.keys(filteredStructure.doc.share || {}).length;
  const totalShareKeysCount = Object.keys(docStructure.doc.share || {}).length;

  return (
    <div className="yjs-debug-container">
      <h1>YJS Debug View</h1>
      <p>Workspace ID: {workspaceId}</p>
      
      <div className="yjs-debug-stats">
        <h2>Client Statistics</h2>
        <div className="yjs-debug-stat-grid">
          <div className="yjs-debug-stat">
            <span className="yjs-debug-stat-value">{clientStats.active}</span>
            <span className="yjs-debug-stat-label">Active Clients</span>
          </div>
          <div className="yjs-debug-stat">
            <span className="yjs-debug-stat-value">{clientStats.inactive}</span>
            <span className="yjs-debug-stat-label">Inactive Clients</span>
          </div>
          <div className="yjs-debug-stat">
            <span className="yjs-debug-stat-value">{clientStats.total}</span>
            <span className="yjs-debug-stat-label">Total Clients</span>
          </div>
          <div className="yjs-debug-stat">
            <span className="yjs-debug-stat-value">{shareKeysCount}/{totalShareKeysCount}</span>
            <span className="yjs-debug-stat-label">Share Keys{isFilterActive ? ' (Filtered)' : ''}</span>
          </div>
        </div>
      </div>
      
      <div className="yjs-debug-filter">
        <label htmlFor="filter-regex">Filter Share Keys: </label>
        <input 
          id="filter-regex" 
          type="text" 
          placeholder="Enter regex pattern..." 
          onChange={handleFilterChange}
          defaultValue={filterRegex}
          ref={filterInputRef}
          className={filterError ? 'filter-error' : ''}
        />
        {filterRegex && (
          <button onClick={clearFilter} className="yjs-debug-clear-filter">
            Clear
          </button>
        )}
        {filterError && <div className="yjs-debug-filter-error">{filterError}</div>}
        {isFilterActive && !filterError && (
          <div className="yjs-debug-filter-active">
            Showing {shareKeysCount} of {totalShareKeysCount} share keys
          </div>
        )}
      </div>
      
      <div className="yjs-debug-controls">
        <button onClick={handleRefresh} className="yjs-debug-refresh">
          Refresh
        </button>
        <button 
          onClick={toggleCleanupMode} 
          className={`yjs-debug-cleanup ${isCleanupMode ? 'active' : ''}`}
        >
          {isCleanupMode ? 'Cancel Cleanup' : 'Start Cleanup'}
        </button>
        {isCleanupMode && (
          <button 
            onClick={cleanupSelectedClients} 
            className="yjs-debug-cleanup-execute"
            disabled={Object.keys(selectedClientsForCleanup).length === 0}
          >
            Clean Up Selected ({Object.keys(selectedClientsForCleanup).length})
          </button>
        )}
      </div>
      
      {isCleanupMode && (
        <div className="yjs-debug-client-list">
          <h3>Client List for Cleanup</h3>
          <p className="yjs-debug-warning">
            Warning: Removing client IDs can cause data loss if they are still active.
            Only remove clients that you are certain are no longer in use.
          </p>
          <div className="yjs-debug-client-section">
            <h4>Active Clients (Currently Connected)</h4>
            {activeClientsArray.length === 0 ? (
              <p>No active clients found.</p>
            ) : (
              <ul className="yjs-debug-client-items">
                {activeClientsArray.map(client => (
                  <li key={`active-${client.clientId}`} className="yjs-debug-client-item">
                    <input
                      type="checkbox"
                      id={`client-${client.clientId}`}
                      checked={!!selectedClientsForCleanup[client.clientId]}
                      onChange={() => toggleClientSelection(client.clientId)}
                    />
                    <label htmlFor={`client-${client.clientId}`}>
                      Client ID: {client.clientId} - User: {client.user?.name || 'Unknown'} - 
                      Device: {client.user?.id || 'Unknown'}
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
          
          <div className="yjs-debug-client-section">
            <h4>Inactive Clients (Potential Cleanup Candidates)</h4>
            {inactiveClientIds.length === 0 ? (
              <p>No inactive clients found.</p>
            ) : (
              <ul className="yjs-debug-client-items">
                {inactiveClientIds.map(clientId => (
                  <li key={`inactive-${clientId}`} className="yjs-debug-client-item">
                    <input
                      type="checkbox"
                      id={`client-${clientId}`}
                      checked={!!selectedClientsForCleanup[clientId]}
                      onChange={() => toggleClientSelection(clientId)}
                    />
                    <label htmlFor={`client-${clientId}`}>
                      Client ID: {clientId} - Items: {filteredStructure.doc.clients[clientId]?.itemsCount || 0}
                    </label>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
      
      {!isCleanupMode && (
        <div className="yjs-debug-tree">
          <TreeNode name="document" value={filteredStructure} isExpanded={true} />
        </div>
      )}
    </div>
  );
};

export default YjsDebugViewComponent;